import { SplitButton } from '@innovyze/stylovyze';
import { Delete } from '@mui/icons-material';
import { FormikProps } from 'formik';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { updateUser } from '@Actions/users';
import { CancelButton } from '@Components/Buttons';
import DeleteUserModal from '@Components/EntitlementsTable/DeleteUserModal';
import { NewUserForm } from '@Components/NewUserForm/NewUserForm';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { MAIN_ROUTES } from '@Types/routes';
import { User } from '@Types/user.types';
import {
	defaultUserErrors,
	useCreateUserContext,
} from '../../contexts/CreateUserContext';
import useValidateUserFromSchema from '../../hooks/useValidateUserFromSchema';
import {
	emailSchema,
	entitlementsSchema,
	nameSchema,
	newUserFormSchemaWithEntitlements,
	phoneNumberSchema,
	roleSchema,
} from '../../schemas/user.schema';
import * as Styles from './UpdateUserPage.styles';

const UpdateUserPage = (): JSX.Element => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const [formikProps, setNewUserFormikProps] = useState<
		Partial<FormikProps<User>>
	>({});
	const { values } = formikProps;
	const { setUserErrors } = useCreateUserContext();
	const validateFromSchema = useValidateUserFromSchema();
	const { initialized } = useSelector(getValue).users.userById;
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const blocked = !!formikProps.values?.blocked;

	const handleUpdateClick = () => {
		newUserFormSchemaWithEntitlements
			.validate(values)
			.then(() => {
				setUserErrors(defaultUserErrors);
				dispatch(updateUser(values as User));
			})
			.catch(() => {
				validateFromSchema(nameSchema, values?.name, 'name');
				validateFromSchema(emailSchema, values?.email, 'email');
				validateFromSchema(roleSchema, values?.role, 'role');
				validateFromSchema(
					phoneNumberSchema,
					values?.phoneNumber,
					'phoneNumber',
				);
				validateFromSchema(
					entitlementsSchema,
					values?.entitlements,
					'entitlements',
				);
			});
	};
	return (
		<Styles.CustomPageWrapper
			title={!initialized || !blocked ? t('Update User') : values?.name}
			applyPadding={true}
			isLoading={!initialized}
			secondary={
				<Styles.CustomSecondaryMenu>
					{initialized && blocked && (
						<Styles.CustomStatusTag
							color="danger"
							text={t('USER LOCKED')}
							width="100%"
						/>
					)}
					<CancelButton
						data-cy="cancelButton"
						onClick={() => {
							history.push(MAIN_ROUTES.UserManagement);
						}}>
						{t('Cancel')}
					</CancelButton>
					<SplitButton
						includeSelectedInMenu={false}
						oneClickMenu
						disabled={blocked || !initialized}
						variant="contained"
						color="primary"
						options={[
							{
								onClick: handleUpdateClick,
								title: t('Update'),
								cy: 'updateUser',
							},
							{
								icon: <Delete data-cy="deleteUser" />,
								onClick: () => {
									setIsDeleteModalOpen(true);
								},
								title: t('Delete'),
							},
						]}
						selectedIcon
					/>
				</Styles.CustomSecondaryMenu>
			}
			headerUnderline>
			<Styles.Container>
				<DeleteUserModal
					setIsModalOpen={setIsDeleteModalOpen}
					isModalOpen={isDeleteModalOpen}
					userID={isDeleteModalOpen ? values?.id : undefined}
					userName={
						isDeleteModalOpen && values?.username
							? values?.username
							: ''
					}
				/>
				<NewUserForm setNewUserFormikProps={setNewUserFormikProps} />
			</Styles.Container>
		</Styles.CustomPageWrapper>
	);
};

export default UpdateUserPage;
